import React, { useState } from 'react';
import { StanToggle } from '@/components';
import { useModal } from '@/hooks/useModal';
import type { MFAConfig } from '@/lib/api/settings/types';
import { useDeactivateMFA, useMFAList } from '../../queries/useMFA';
import { stanNotify } from '@/lib/stanNotify';
import { handleApiError } from '@/lib/utils/apiErrorHandler';
import { MFAMethodsTable } from './MFAMethodsTable';
import { RenameMFAModal } from './modals/RenameMFAModal';
import { DeleteMFAModal } from './modals/DeleteMFAModal';
import { MFA_STATUS } from '../../constants/mfa';

interface TwoFactorAuthSectionProps {
    onConfigureMFA: () => void;
    onMFASetupComplete?: () => void;
}

export const TwoFactorAuthSection: React.FC<TwoFactorAuthSectionProps> = ({ onConfigureMFA }) => {
    const { data: mfaConfigs = [], isLoading, refetch } = useMFAList();
    const { mutate: deactivateMFA, isPending: isDeactivating } = useDeactivateMFA();

    const renameModal = useModal();
    const deleteModal = useModal();
    const [selectedConfig, setSelectedConfig] = useState<MFAConfig | null>(null);

    // MFA is enabled if there's at least one active config
    const mfaEnabled = mfaConfigs.some(config => config.status === MFA_STATUS.ACTIVE);
    const showTable = mfaEnabled && mfaConfigs.length > 0;

    const handleToggle = () => {
        if (mfaEnabled) {
            // Turn off MFA - deactivate all configs
            deactivateMFA(undefined, {
                onSuccess: () => {
                    stanNotify({
                        type: 'success',
                        title: 'Success!',
                        text: 'Two-factor authentication has been disabled.',
                    });
                    refetch();
                },
                onError: err => {
                    handleApiError(err, 'Failed to disable 2FA');
                },
            });
        } else {
            // Turn on MFA - open configuration modal
            onConfigureMFA();
        }
    };

    const handleRename = (config: MFAConfig) => {
        setSelectedConfig(config);
        renameModal.open();
    };

    const handleDelete = (config: MFAConfig) => {
        setSelectedConfig(config);
        deleteModal.open();
    };

    const handleModalSuccess = () => {
        refetch();
    };

    return (
        <>
            <div className='py-3'>
                <div className='flex items-start gap-3'>
                    <div className='mt-1 flex items-center'>
                        <StanToggle
                            checked={mfaEnabled}
                            onChange={handleToggle}
                            disabled={isLoading || isDeactivating}
                            toggleSize='sm'
                        />
                    </div>
                    <div className='flex-1'>
                        <h4 className='mb-1'>Two-Factor Verification</h4>
                        <p className='text-gray text-caption-1-regular'>
                            Add an additional layer of security to your account during login.
                        </p>
                    </div>
                </div>

                {showTable && (
                    <MFAMethodsTable
                        configs={mfaConfigs}
                        isLoading={isLoading}
                        onRename={handleRename}
                        onDelete={handleDelete}
                    />
                )}
            </div>

            <RenameMFAModal
                isOpen={renameModal.isOpen}
                onClose={renameModal.close}
                config={selectedConfig}
                onSuccess={handleModalSuccess}
            />

            <DeleteMFAModal
                isOpen={deleteModal.isOpen}
                onClose={deleteModal.close}
                config={selectedConfig}
                onSuccess={handleModalSuccess}
            />
        </>
    );
};
